import type {RevaLifeCycleState} from "@reva/shared/lifecycle-state";

export interface RevaChartStyle {
    colors: string[];
    colorLow: string;
    colorAvg: string;
    colorHigh: string;
    fillLow: string;
    fillHigh: string;
    grid: string;
    ticks: string;
    tension: number;
    tooltipColorBoxBorder: number;
    tooltipColorBoxBorderColor: string;
}

/**
 * Steuert die Glättung der Linien in Chart.js-Liniendiagrammen (Catmull-Rom-Interpolation).
 * 0 = gerade Linien zwischen den Datenpunkten, 1 = maximale Krümmung.
 * Die Datenpunkte selbst werden nicht verändert, nur die visuelle Darstellung zwischen ihnen.
 */
const CHART_LINE_TENSION = 0.1;

/** @type {RevaChartStyle} */
export const CHART_STYLE_LIGHT: RevaChartStyle = {
    colors: ["#6600E2", "#272D3F", "#5DA5DA", "#9d9d9d", "#b477ff"],
    colorLow: "rgba(255,255,255,0)",
    fillLow: "rgba(102,0,226,0.1)",
    colorAvg: "#6600E2",
    colorHigh: "rgba(255,255,255,0)",
    fillHigh: "rgba(102,0,226,0.1)",
    grid: "--surface-border",
    ticks: "--text-color-secondary",
    tension: CHART_LINE_TENSION,
    tooltipColorBoxBorder: 2,
    tooltipColorBoxBorderColor: "#ffffff",
};

/** @type {RevaChartStyle} */
export const CHART_STYLE_DARK: RevaChartStyle = {
    colors: ["#6600E2", "#e5e5e5", "#5DA5DA", "#5e5e5e", "#b477ff"],
    colorLow: "rgba(255,255,255,0)",
    fillLow: "rgba(125,27,248,0.2)",
    colorAvg: "#6600E2",
    colorHigh: "rgba(255,255,255,0)",
    fillHigh: "rgba(125,27,248,0.2)",
    grid: "--surface-d",
    ticks: "--text-color-secondary",
    tension: CHART_LINE_TENSION,
    tooltipColorBoxBorder: 0,
    tooltipColorBoxBorderColor: "rgba(255,255,255,0)",
};

/**
 * Get corresponding Chart style.
 */
export function getChartStyle(isDarkTheme: boolean): RevaChartStyle {
    return isDarkTheme ? CHART_STYLE_DARK : CHART_STYLE_LIGHT;
}

/**
 * Primäre Balkenfarbe der Startseiten-Statistiken (Finalisierungen der Historie, Altersverteilung des
 * Auftragspools). Die helle Lila-Variante hält die Balken auf dunklem Grund lesbar (das Marken-Lila ist
 * dort zu dunkel).
 */
export function getPrimaryBarColor(isDarkTheme: boolean): string {
    const style = getChartStyle(isDarkTheme);
    return getColor(isDarkTheme ? style.colors[4] : style.colors[0]);
}

/**
 * Aufgelöste Diagramm-Farben für die beiden Snapshot-Typen (Finalisierung / Vorab-Finalisierung).
 * Kapselt das Snapshot-Typ→Paletten-Index-Mapping an einer Stelle, damit die Balken im
 * Statistik-Diagramm und die farbigen Checkbox-Swatches der Historie-Liste garantiert
 * übereinstimmen.
 */
export function getSnapshotTypeColors(isDarkTheme: boolean): {colorFinal: string; colorPreFinal: string} {
    return {
        colorFinal: getPrimaryBarColor(isDarkTheme),
        colorPreFinal: getColor(getChartStyle(isDarkTheme).colors[2]),
    };
}

/**
 * Farbzuordnung der fünf REVA-Lifecycle-Stati für die Status-Filter-Checkboxen (und die Farbpunkte
 * in der Statusspalte) der Bewertungsliste. Semantisch entlang des Lebenszyklus (Grau → Amber →
 * … → Rot), wobei PreFinal und Final bewusst exakt die Farben des Snapshot-Typ-Diagramms
 * (siehe {@link getSnapshotTypeColors}) übernehmen, damit Liste und Historie/Statistik konsistent sind.
 */
export function getLifeCycleStateColors(isDarkTheme: boolean): Record<RevaLifeCycleState, string> {
    const {colorFinal, colorPreFinal} = getSnapshotTypeColors(isDarkTheme);
    return {
        New: "#9d9d9d", // Grau — neu, noch nicht bearbeitet
        DataInput: isDarkTheme ? "#e0a63b" : "#c07d0a", // Amber — in Dateneingabe
        PreFinal: colorPreFinal, // wie im Diagramm (Vorläufig finalisiert)
        Final: colorFinal, // wie im Diagramm (Finalisiert)
        Correction: isDarkTheme ? "#f07070" : "#d64545", // Rot — in Korrektur
    };
}

/**
 * Get color for chartStyle.
 * @param {string} colorStr color String
 * @return {*|string} resolved color
 */
export function getColor(colorStr: string | null): string {
    if (!colorStr) {
        console.warn(`Invalid colorStr: ${colorStr}`);
        return "#000000";
    }
    if (colorStr.startsWith("--")) {
        return getComputedStyle(document.documentElement).getPropertyValue(colorStr);
    }
    if (!colorStr.startsWith("#") && !colorStr.startsWith("rgb(") && !colorStr.startsWith("rgba(")) {
        console.warn(`Invalid colorStr: ${colorStr}`);
    }
    return colorStr;
}
